// this code is modified from https://github.com/juliankrispel/use-text-selection/blob/master/src/index.tsx
// hacked in with some code from components/BillAnnotationsFromSelection
// under the MIT license
import { useCallback, useLayoutEffect, useState } from 'react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { debounce } from 'throttle-debounce';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import rangy from 'rangy';

type ClientRect = Record<keyof Omit<DOMRect, 'toJSON'>, number>;

function roundValues(_rect: ClientRect) {
  const rect = {
    ..._rect,
  };
  // eslint-disable-next-line no-restricted-syntax
  for (const key of Object.keys(rect)) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    rect[key] = Math.round(rect[key]);
  }
  return rect;
}

function shallowDiff(prev: any, next: any) {
  if (prev != null && next != null) {
    // eslint-disable-next-line no-restricted-syntax
    for (const key of Object.keys(next)) {
      if (prev[key] !== next[key]) {
        return true;
      }
    }
  } else if (prev !== next) {
    return true;
  }
  return false;
}

type TextSelectionState = {
  clientRect?: ClientRect;
  isCollapsed?: boolean;
  textContent?: string;
};

const defaultState: TextSelectionState = {};

/**
 * useTextSelection(ref)
 *
 * @description
 * hook to get information about the current text selection
 *
 */
export function useTextSelection(target?: HTMLElement) {
  const [{ clientRect, isCollapsed, textContent }, setState] =
    useState<TextSelectionState>(defaultState);

  const handler = useCallback(() => {
    let newRect: ClientRect;
    const selection = window.getSelection();
    const newState: TextSelectionState = {};

    if (selection == null || !selection.rangeCount) {
      setState(newState);
      return;
    }

    const range = selection.getRangeAt(0);

    if (target != null && !target.contains(range.commonAncestorContainer)) {
      setState(newState);
      return;
    }

    if (range == null) {
      setState(newState);
      return;
    }

    const contents = range.cloneContents();

    // HACK TIME
    let textSelection = '';
    if (range && range.startContainer && range.startContainer.textContent) {
      textSelection = range.startContainer.textContent.slice(
        range.startOffset,
        range.endOffset,
      );
    }
    if (
      range.commonAncestorContainer.nodeType !== range.commonAncestorContainer.TEXT_NODE
    ) {
      const rangySelection = rangy.getSelection();
      const rangyRange = rangySelection.getRangeAt(0);
      const rangyNodes = rangyRange.getNodes(
        [3],
        (node: { parentElement: { classList: { contains: (arg0: string) => any } } }) =>
          !node.parentElement.classList ||
          !node.parentElement.classList.contains('line-number'),
      );
      const selectionTexts = rangyNodes.map(
        (node: { textContent: any }) => node.textContent,
      );
      if (selectionTexts.length > 0) {
        selectionTexts[0] = selectionTexts[0].slice(range.startOffset);
        selectionTexts[selectionTexts.length - 1] = selectionTexts[
          selectionTexts.length - 1
        ].slice(0, range.endOffset);
        textSelection = selectionTexts.join(' ');
      }
    }

    // we're just joining all texts by ' ' as a hack to account for newlines (which aren't represented in the array)
    // so let's just kill any spaces greater than one
    // ADDITION LATER: kill any instance of more than one space in a row as well
    textSelection = textSelection.replace(/'\s+'|\s+/g, ' ');

    if (contents.textContent != null) {
      // newState.textContent = contents.textContent;
      newState.textContent = textSelection;
    }

    const rects = range.getClientRects();

    // This whole section needs extraordinary cleanup
    const bottomPadding = 70;
    if (rects.length === 0 && range.commonAncestorContainer != null) {
      const el = range.commonAncestorContainer as HTMLElement;
      const rect = el.getBoundingClientRect();
      if (target) {
        // adjust for container
        const containingElRect = target.getBoundingClientRect();
        newRect = roundValues(
          {
            ...rect,
            top: rect.bottom - containingElRect.top + bottomPadding,
            left: rect.left - containingElRect.left + rect.width / 2,
          }.toJSON(),
        );
      } else {
        newRect = roundValues(rect.toJSON());
      }
    } else {
      if (rects.length < 1) return;
      if (target) {
        // adjust for container
        const containingElRect = target.getBoundingClientRect();
        newRect = {
          ...rects[0],
          top: rects[0].bottom - containingElRect.top + bottomPadding,
          left: rects[0].left - containingElRect.left + rects[0].width / 2,
        };
      } else {
        newRect = roundValues(rects[0].toJSON());
      }
    }
    if (shallowDiff(clientRect, newRect)) {
      newState.clientRect = newRect;
    }
    newState.isCollapsed = range.collapsed;

    setState(newState);
  }, [target]);

  useLayoutEffect(() => {
    const DEFAULT_DEBOUNCE_TIME_MS = 300;
    const debouncedHandler = debounce(DEFAULT_DEBOUNCE_TIME_MS, handler);
    document.addEventListener('selectionchange', debouncedHandler);
    // document.addEventListener('keydown', debouncedHandler);
    // document.addEventListener('keyup', debouncedHandler);
    // window.addEventListener('resize', debouncedHandler);

    return () => {
      document.removeEventListener('selectionchange', debouncedHandler);
      // document.removeEventListener('keydown', debouncedHandler);
      // document.removeEventListener('keyup', debouncedHandler);
      // window.removeEventListener('resize', debouncedHandler);
    };
  }, [target]);

  return {
    clientRect,
    isCollapsed,
    textContent,
  };
}
